import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, CircularProgress,
  Chip, Avatar, Divider, Tooltip, IconButton, Button, Paper,
  TextField, Select, MenuItem, InputAdornment, FormControl, InputLabel,
  Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack,
} from '@mui/material';
import {
  EventNote as DutyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Person as PersonIcon,
  PersonOff as AbsentIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarnIcon,
  SwapHoriz as SwapIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';

// ─────────────────────────────────────────────────────────────────────────────
// Theme tokens — Orange palette matching TeacherSidebar (#f59e0b / #d97706)
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  ink:      '#1A0F00',
  slate:    '#3D2400',
  orange:   '#EA580C',
  orangeD:  '#C2410C',
  orangeL:  '#FB923C',
  teal:     '#0D9488',
  emerald:  '#059669',
  amber:    '#D97706',
  rose:     '#E11D48',
  violet:   '#7C3AED',
  border:   '#FED7AA',
  surface:  '#FFF7ED',
  card:     '#FFFFFF',
  muted:    '#92400E',
  faint:    '#C2956A',
  ghost:    '#FFFBF5',
};
const ROW_OPTIONS = [5, 10, 15, 25];

const BASE  = process.env.REACT_APP_BASE_URL;
const tok   = () => sessionStorage.getItem('token');
const authH = () => ({ Authorization: tok() });
const postH = () => ({ 'Content-Type': 'application/json', Authorization: tok() });
const safeJ = async (r) => { try { return await r.json(); } catch { return null; } };

const apiGet  = (path) => fetch(`${BASE}${path}`, { headers: authH() });
const apiPost = (path, body) => fetch(`${BASE}${path}`, {
  method: 'POST', headers: postH(), body: JSON.stringify(body),
});

const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const todayStr = () => new Date().toISOString().split('T')[0];

// ─────────────────────────────────────────────────────────────────────────────
// Shared atoms
// ─────────────────────────────────────────────────────────────────────────────
const TH = ({ children, align = 'left', width }) => (
  <TableCell align={align} sx={{
    bgcolor: '#FFF0E0',
    fontWeight: 700,
    fontSize: '0.72rem',
    color: T.slate,
    py: 1.4,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${T.border}`,
    ...(width ? { width } : {}),
  }}>
    {children}
  </TableCell>
);

const StatusPill = ({ status }) => {
  const map = {
    Confirmed:  { bg: '#D1FAE5', color: T.emerald,  dot: '#10B981' },
    Cancelled:  { bg: '#FFE4E6', color: T.rose,     dot: '#F43F5E' },
    Pending:    { bg: '#FEF3C7', color: T.amber,    dot: '#F59E0B' },
    Preferred:  { bg: '#EDE9FE', color: T.violet,   dot: '#8B5CF6' },
    Available:  { bg: '#FFF0E0', color: T.orange,   dot: T.orangeL },
  };
  const s = map[status] || { bg: '#F1F5F9', color: T.muted, dot: T.faint };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
      bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.68rem',
      px: 1, py: 0.35, borderRadius: '20px', lineHeight: 1.5, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: s.dot, flexShrink: 0 }} />
      {status || '—'}
    </Box>
  );
};

const SearchBar = ({ value, onChange, placeholder = 'Search…', width = 260 }) => (
  <TextField size="small" placeholder={placeholder} value={value}
    onChange={e => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ fontSize: 15, color: T.faint }} />
        </InputAdornment>
      ),
    }}
    sx={{
      width,
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontSize: '0.83rem',
        bgcolor: T.ghost,
        '&:hover fieldset': { borderColor: T.orange },
        '&.Mui-focused fieldset': { borderColor: T.orange, borderWidth: 1.5 },
      },
    }}
  />
);

const Pager = ({ total, page, rowsPerPage, onPage, onRows }) => {
  const pages = Math.max(Math.ceil(total / rowsPerPage), 1);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}`, flexWrap: 'wrap', gap: 1,
      bgcolor: T.ghost,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>Rows per page:</Typography>
        <Select value={rowsPerPage} onChange={e => { onRows(Number(e.target.value)); onPage(1); }}
          size="small"
          sx={{ fontSize: '0.78rem', height: 28, minWidth: 60, borderRadius: '8px', bgcolor: T.card }}>
          {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
          {total === 0 ? '0' : `${Math.min((page - 1) * rowsPerPage + 1, total)}–${Math.min(page * rowsPerPage, total)}`} of {total}
        </Typography>
        <IconButton size="small" onClick={() => onPage(p => p - 1)} disabled={page <= 1}
          sx={{ '&:not(:disabled)': { color: T.orange } }}>
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onPage(p => p + 1)} disabled={page >= pages}
          sx={{ '&:not(:disabled)': { color: T.orange } }}>
          <NextIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function MyDuties() {
  const [teachers,          setTeachers]          = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [branchId,          setBranchId]          = useState(null);

  const [duties,      setDuties]      = useState([]);
  const [loadingDuty, setLoadingDuty] = useState(false);
  const [dutyFrom,    setDutyFrom]    = useState(todayStr());
  const [dutySearch,  setDutySearch]  = useState('');
  const [dutyPage,    setDutyPage]    = useState(1);
  const [dutyRows,    setDutyRows]    = useState(10);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const bid = sessionStorage.getItem('branchId');
    if (bid) setBranchId(Number(bid));
    fetchTeachers();
  }, []);

  useEffect(() => {
    if (branchId) loadDuties();
  }, [dutyFrom, selectedTeacherId, branchId]);

  // ── Fetch Teachers ───────────────────────────────────────────────────────
  const fetchTeachers = async () => {
    try {
      const res  = await apiPost(`/AssignmentCreate/GetTeacher`, {});
      const data = await safeJ(res);
      setTeachers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch teachers', e);
    }
  };

  // ── Load Duties ──────────────────────────────────────────────────────────
  const loadDuties = useCallback(async () => {
    if (!branchId) return;
    setLoadingDuty(true);
    try {
      const params = new URLSearchParams();
      if (dutyFrom)           params.set('fromDate',       dutyFrom);
      if (selectedTeacherId)  params.set('ProxyTeacherId', selectedTeacherId);
      params.set('Branch_id', branchId);
      const res = await apiGet(`/adv/proxy/my-duties?${params}`);
      const d   = await safeJ(res);
      setDuties(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load duties', 'error');
    } finally {
      setLoadingDuty(false);
    }
  }, [dutyFrom, selectedTeacherId, branchId]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const confirmedCount = duties.filter(d => (d.proxyStatus ?? d.ProxyStatus) === 'Confirmed').length;
  const pendingCount   = duties.filter(d => (d.proxyStatus ?? d.ProxyStatus) === 'Pending').length;

  const filteredDuties = useMemo(() =>
    duties.filter(r =>
      !dutySearch ||
      (r.subjectName       ?? '').toLowerCase().includes(dutySearch.toLowerCase()) ||
      (r.className         ?? '').toLowerCase().includes(dutySearch.toLowerCase()) ||
      (r.absentTeacherName ?? '').toLowerCase().includes(dutySearch.toLowerCase())
    ), [duties, dutySearch]);

  const pagedDuties = useMemo(() =>
    filteredDuties.slice((dutyPage - 1) * dutyRows, dutyPage * dutyRows),
    [filteredDuties, dutyPage, dutyRows]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    // ── Outer box: matches TimetablePage (Create Time Table) layout pattern ──
    <Box component="main" sx={{
      width: { md: `calc(100% - 240px)` },
      ml: { md: `240px` },
      mt: { xs: 0, sm: '64px' },
      p: { xs: 1, md: 3 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f5f5, #ffffff)',
    }}>

      {/* ── Page Header — orange gradient matching sidebar ── */}
      <Box sx={{
        background: 'linear-gradient(145deg, #f59e0b 0%, #d97706 100%)',
        py: 2.5,
        px: { xs: 2, md: 4 },
        mb: 3,
        // NO mx or mt — DashboardMain's <main> already has padding: 0
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(217,119,6,0.2)',
        '&::before': {
          content: '""', position: 'absolute', top: -40, right: -40,
          width: 160, height: 160, borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)', pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -30, left: '40%',
          width: 120, height: 120, borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)', pointerEvents: 'none',
        },
      }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: 2,
          position: 'relative', zIndex: 1,
        }}>

          {/* ── Title ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.22)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: '10px', p: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <DutyIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.2, letterSpacing: -0.3 }}>
                Proxy Duties
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.75rem', mt: 0.2 }}>
                View your assigned proxy duties
              </Typography>
            </Box>
          </Box>

          {/* ── KPI chips ── */}
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            {[
              { label: 'Total',     value: duties.length,  bg: 'rgba(255,255,255,0.18)', border: 'rgba(255,255,255,0.35)', color: '#fff'    },
              { label: 'Confirmed', value: confirmedCount, bg: 'rgba(16,185,129,0.2)',   border: 'rgba(52,211,153,0.4)',   color: '#6EE7B7' },
              { label: 'Pending',   value: pendingCount,   bg: 'rgba(251,191,36,0.2)',   border: 'rgba(251,191,36,0.45)',  color: '#FDE68A' },
            ].map((k, i) => (
              <Box key={i} sx={{
                bgcolor: k.bg, border: `1px solid ${k.border}`,
                borderRadius: '10px', px: 1.8, py: 0.8,
                textAlign: 'center', minWidth: 58,
                backdropFilter: 'blur(4px)',
              }}>
                <Typography sx={{ fontWeight: 900, color: k.color, fontSize: '1.2rem', lineHeight: 1 }}>
                  {k.value}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.6rem', fontWeight: 600, mt: 0.2 }}>
                  {k.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Main content card ── */}
      <Card sx={{
        borderRadius: 2,
        boxShadow: '0 10px 30px rgba(217,119,6,0.25)',
        border: `1px solid ${T.border}`,
        overflow: 'hidden',
      }}>

        {/* ── Filter bar ── */}
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, pb: '12px !important' }}>
          <Grid container spacing={2} alignItems="center">

            {/* Teacher dropdown */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={teachers.length === 0}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>Teacher</InputLabel>
                <Select
                  value={selectedTeacherId}
                  label="Teacher"
                  onChange={e => { setSelectedTeacherId(e.target.value); setDutyPage(1); }}
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 14, color: T.faint, ml: 0.3 }} />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: '10px', fontSize: '0.83rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.orange },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.orange },
                  }}
                  MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: 0.5 } } }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.82rem', color: T.muted }}>
                    <em>All Teachers</em>
                  </MenuItem>
                  {teachers.map(t => (
                    <MenuItem
                      key={t.employeeId ?? t.EmployeeId}
                      value={t.employeeId ?? t.EmployeeId}
                      sx={{ fontSize: '0.82rem' }}
                    >
                      {t.employeeName ?? t.EmployeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* From date */}
            <Grid item xs={12} sm={3}>
              <TextField label="From Date" type="date" size="small" fullWidth
                value={dutyFrom} onChange={e => { setDutyFrom(e.target.value); setDutyPage(1); }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '&:hover fieldset': { borderColor: T.orange },
                    '&.Mui-focused fieldset': { borderColor: T.orange },
                  },
                }} />
            </Grid>

            {/* Search */}
            <Grid item xs={12} sm={3}>
              <SearchBar value={dutySearch} onChange={v => { setDutySearch(v); setDutyPage(1); }}
                placeholder="Search subject, class…" width="100%" />
            </Grid>

            {/* Reload */}
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                onClick={loadDuties} fullWidth
                sx={{
                  borderRadius: '10px', fontWeight: 700,
                  background: 'linear-gradient(145deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(217,119,6,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(145deg, #d97706 0%, #b45309 100%)',
                    boxShadow: '0 4px 12px rgba(217,119,6,0.4)',
                  },
                }}>
                Reload
              </Button>
            </Grid>
          </Grid>
        </CardContent>

        <Divider sx={{ borderColor: T.border }} />

        {/* ── Table ── */}
        {loadingDuty ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flexDirection: 'column', gap: 2 }}>
            <CircularProgress sx={{ color: T.orange }} />
            <Typography variant="body2" color="text.secondary">Loading duties…</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TH width={44}>#</TH>
                    <TH width={120}>Proxy Date</TH>
                    <TH>Absent Teacher</TH>
                    <TH>Class</TH>
                    <TH>Subject</TH>
                    <TH>Period</TH>
                    <TH align="center" width={110}>Status</TH>
                    <TH>Remarks</TH>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDuties.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            background: 'linear-gradient(145deg, #f59e0b 0%, #d97706 100%)',
                            borderRadius: '50%', width: 64, height: 64,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 16px rgba(245,158,11,0.3)',
                          }}>
                            <DutyIcon sx={{ fontSize: 32, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 700, color: T.ink }}>
                            {duties.length === 0 ? 'No proxy duties found' : 'No results match your search'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {duties.length === 0 ? 'Try adjusting the date or teacher filter.' : 'Clear your search to see all results.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : pagedDuties.map((rec, idx) => {
                    const rowNum = (dutyPage - 1) * dutyRows + idx + 1;
                    return (
                      <TableRow key={rec.id ?? rec.Id ?? idx} hover
                        sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#FFF7F0' } }}>
                        <TableCell sx={{ color: T.faint, fontSize: '0.7rem' }}>{rowNum}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.78rem' }}>
                            {fmt(rec.proxyDate ?? rec.ProxyDate)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.64rem' }}>
                            {rec.dayName ?? rec.DayName ?? ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{
                              width: 28, height: 28,
                              background: 'linear-gradient(145deg, #f59e0b 0%, #d97706 100%)',
                              color: '#fff', fontSize: '0.7rem', fontWeight: 800,
                            }}>
                              {(rec.absentTeacherName ?? rec.AbsentTeacherName ?? '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {rec.absentTeacherName ?? rec.AbsentTeacherName ?? '—'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {rec.className ?? rec.ClassName ?? '—'} {rec.sectionName ?? rec.SectionName ?? ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.8rem' }}>
                            {rec.subjectName ?? rec.SubjectName ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {rec.periodName ?? rec.PeriodName ?? '—'}
                          </Typography>
                          {(rec.startTime ?? rec.StartTime) && (
                            <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.64rem' }}>
                              {rec.startTime ?? rec.StartTime}–{rec.endTime ?? rec.EndTime}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <StatusPill status={rec.proxyStatus ?? rec.ProxyStatus} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.7rem' }}>
                            {rec.remarks ?? rec.Remarks ?? '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager total={filteredDuties.length} page={dutyPage} rowsPerPage={dutyRows}
              onPage={setDutyPage} onRows={setDutyRows} />
          </>
        )}
      </Card>

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.sev}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}